/**
 * Subscription Status Banner
 *
 * Full-width contextual banner at the top of the Wallet page.
 * Clearly communicates the current subscription state to the agent.
 *
 * Uses brand-consistent styling:
 * - active: Navy/gold tones (positive)
 * - cancelling/paused: Warm muted tones (attention)
 * - cancelled: Muted neutral
 * - no_subscription: Soft brand accent
 */

import { AlertTriangle, CheckCircle2, XCircle, PauseCircle, PlayCircle, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";

export type SubscriptionState = "active" | "cancelling" | "paused" | "cancelled" | "no_subscription";

interface SubscriptionStatusBannerProps {
  state: SubscriptionState;
  nextBillingDate?: Date;
  cancelsAt?: Date;
  onManageBilling?: () => void;
  onResume?: () => void;
  loading?: boolean;
  pausedReason?: string;
}

const bannerConfig: Record<SubscriptionState, {
  icon: typeof CheckCircle2;
  bgClass: string;
  iconClass: string;
  textClass: string;
}> = {
  active: {
    icon: CheckCircle2,
    bgClass: "bg-primary/5 border-primary/20",
    iconClass: "text-primary",
    textClass: "text-foreground",
  },
  cancelling: {
    icon: AlertTriangle,
    bgClass: "bg-accent/10 border-accent/30",
    iconClass: "text-accent-foreground",
    textClass: "text-foreground",
  },
  paused: {
    icon: PauseCircle,
    bgClass: "bg-destructive/5 border-destructive/20",
    iconClass: "text-destructive",
    textClass: "text-foreground",
  },
  cancelled: {
    icon: XCircle,
    bgClass: "bg-muted border-border",
    iconClass: "text-muted-foreground",
    textClass: "text-muted-foreground",
  },
  no_subscription: {
    icon: CreditCard,
    bgClass: "bg-primary/5 border-primary/15",
    iconClass: "text-primary",
    textClass: "text-foreground",
  },
};

function getMessage(
  state: SubscriptionState,
  nextBillingDate?: Date,
  cancelsAt?: Date,
  pausedReason?: string
): { title: string; description: string } {
  switch (state) {
    case "active":
      return {
        title: "Subscription Active",
        description: nextBillingDate
          ? `Next billing date: ${format(nextBillingDate, "MMMM d, yyyy")}`
          : "Your subscription is active and leads are being delivered.",
      };
    case "cancelling":
      return {
        title: "Cancellation Scheduled",
        description: cancelsAt
          ? `Your subscription will end on ${format(cancelsAt, "MMMM d, yyyy")}. Lead delivery continues until then.`
          : "Your subscription is scheduled to cancel at the end of the current billing period.",
      };
    case "paused":
      if (pausedReason === "agent_requested") {
        return {
          title: "Delivery Paused",
          description: "You paused lead delivery. Resume anytime to start receiving leads again. Auto-resumes after 14 days.",
        };
      }
      if (pausedReason === "admin_requested") {
        return {
          title: "Delivery Paused",
          description: "Your delivery has been paused by your account manager. Contact support if you have questions.",
        };
      }
      return {
        title: "Payment Issue",
        description: "A recent payment failed. Please update your payment method to resume lead delivery.",
      };
    case "cancelled":
      return {
        title: "Subscription Ended",
        description: "Your subscription has been cancelled. Contact your account manager to start a new plan.",
      };
    case "no_subscription":
      return {
        title: "Prepaid Package",
        description: "You have a prepaid lead package. Leads are delivered until your package is fulfilled.",
      };
  }
}

export function SubscriptionStatusBanner({
  state,
  nextBillingDate,
  cancelsAt,
  onManageBilling,
  onResume,
  loading,
  pausedReason,
}: SubscriptionStatusBannerProps) {
  // Use accent/muted styling for agent/admin-requested pauses; destructive only for payment failure
  const isNonPaymentPause = state === "paused" && (pausedReason === "agent_requested" || pausedReason === "admin_requested");
  const config = isNonPaymentPause
    ? {
        icon: PauseCircle,
        bgClass: "bg-accent/10 border-accent/30",
        iconClass: "text-accent-foreground",
        textClass: "text-foreground",
      }
    : bannerConfig[state];
  const { title, description } = getMessage(state, nextBillingDate, cancelsAt, pausedReason);
  const Icon = config.icon;

  // Show payment CTA only for payment-failure pauses
  const isPaymentPause = state === "paused" && (!pausedReason || pausedReason === "payment_failed");
  const showPaymentCTA = isPaymentPause && onManageBilling;
  // Show resume CTA for agent-requested or admin-requested pauses
  const showResumeCTA = state === "paused" && (pausedReason === "agent_requested" || pausedReason === "admin_requested") && onResume;

  return (
    <div className={`rounded-xl border p-4 ${config.bgClass}`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${config.iconClass}`} />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${config.textClass}`}>{title}</p>
          <p className={`text-sm mt-0.5 ${config.textClass} opacity-70`}>{description}</p>
        </div>
        {showPaymentCTA && (
          <Button
            size="sm"
            variant="outline"
            className="flex-shrink-0"
            onClick={onManageBilling}
            disabled={loading}
          >
            <CreditCard className="w-4 h-4 mr-1.5" />
            Update Payment
          </Button>
        )}
        {showResumeCTA && (
          <Button
            size="sm"
            variant="default"
            className="flex-shrink-0"
            onClick={onResume}
            disabled={loading}
          >
            <PlayCircle className="w-4 h-4 mr-1.5" />
            Resume Delivery
          </Button>
        )}
      </div>
    </div>
  );
}
